import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/contexts/TradeAuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import SendDocumentModal from "@/pages/trade/SendDocumentModal";
import CallPrompt from "@/pages/trade/CallPrompt";
import ContactAudioRecordButton from "@/pages/trade/ContactAudioRecordButton";
import RecordedCallButton from "@/pages/trade/RecordedCallButton";
import {
  Plus, Trash2, Edit2, X, Mail, MessageSquare,
  MessageCircle, Send, Receipt, Zap, Save, Paperclip, Calculator, Star
} from "lucide-react";
import { toast } from "sonner";

const waNumber = (num) => (num || "").replace(/[^0-9]/g, "");
const tgLink = (t) => {
  const v = (t || "").trim();
  if (!v) return "";
  if (v.startsWith("@")) return `https://t.me/${v.slice(1)}`;
  return `https://t.me/+${v.replace(/[^0-9]/g, "")}`;
};

const EMPTY_FORM = { name: "", organization: "", phone: "", email: "", whatsapp: "", telegram: "", notes: "" };

const ContactList = ({ type, title, subtitle, orgLabel, orgPlaceholder, icon: Icon, emailSubject, children, compact }) => {
  const { API, authHeader } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [docsTarget, setDocsTarget] = useState(null);

  const fetchData = () => {
    axios.get(`${API}/trade/contacts?type=${type}`, { headers: authHeader() })
      .then(res => setContacts(res.data))
      .catch(e => console.error(e))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, [type]);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ ...EMPTY_FORM, ...c });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!form.phone && !form.email && !form.whatsapp && !form.telegram) {
      toast.error("Add at least one way to reach them (phone, email, WhatsApp or Telegram)");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, contact_type: type };
      if (editing) {
        await axios.put(`${API}/trade/contacts/${editing.id}`, payload, { headers: authHeader() });
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/trade/contacts`, payload, { headers: authHeader() });
        toast.success("Contact saved");
      }
      setModalOpen(false);
      fetchData();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await axios.delete(`${API}/trade/contacts/${id}`, { headers: authHeader() });
      toast.success("Contact deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className={`trade-section ${compact ? "pt-4" : "pt-32 lg:pt-20"} pb-20 min-h-screen`}>
      <div className="lg:ml-64 px-6 md:px-12 py-10 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-start justify-between flex-wrap gap-4 mb-10"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.3em] trade-gold mb-3">Key Contacts</p>
            <h1 className="text-4xl md:text-5xl font-serif font-bold">{title}</h1>
            <p className="text-white/50 mt-2">{subtitle}</p>
          </div>
          <Button onClick={openAdd} className="trade-btn-primary px-6 py-6 rounded-sm" data-testid="add-contact-btn">
            <Plus size={18} className="mr-2" /> Add {type === "accountant" ? "Accountant" : type === "important" ? "Person" : "Helper"}
          </Button>
        </motion.div>

        {loading ? (
          <div className="text-center p-16 text-white/40">Loading...</div>
        ) : contacts.length === 0 ? (
          <div className="trade-card rounded-sm p-16 text-center">
            <Icon className="w-16 h-16 trade-gold mx-auto mb-4 opacity-40" strokeWidth={1} />
            <h3 className="text-xl font-serif mb-2">No {type === "accountant" ? "accountant" : type === "important" ? "important people" : "helpers"} saved yet</h3>
            <p className="text-white/50 mb-6">Add their contact info to reach them in one tap</p>
            <Button onClick={openAdd} className="trade-btn-primary rounded-sm">
              <Plus size={16} className="mr-2" /> Add Now
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {contacts.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="trade-card rounded-sm p-6"
                data-testid={`contact-card-${c.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
                      <Icon className="w-6 h-6 trade-gold" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-serif font-semibold text-lg">{c.name}</h3>
                      {c.organization && <p className="text-xs text-white/60">{c.organization}</p>}
                      {c.phone && <p className="text-[11px] text-white/50 mt-0.5" data-testid={`contact-phone-${c.id}`}>{c.phone}</p>}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => openEdit(c)}
                      className="p-2 text-white/50 hover:text-[#D4AF37] transition-colors"
                      data-testid={`edit-contact-${c.id}`}
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="p-2 text-red-400/60 hover:text-red-400 transition-colors"
                      data-testid={`delete-contact-${c.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                {c.notes && <p className="text-xs text-white/50 mb-4 whitespace-pre-wrap">{c.notes}</p>}

                {/* One-tap communication */}
                <div className="flex flex-wrap gap-2 pt-4 border-t border-white/5">
                  {c.phone && (
                    <CallPrompt
                      phone={c.phone}
                      name={c.name}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
                      testid={`call-contact-${c.id}`}
                    />
                  )}
                  <ContactAudioRecordButton
                    phone={c.phone}
                    name={c.name}
                    className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                    testid={`audio-contact-${c.id}`}
                  />
                  {c.phone && (
                    <RecordedCallButton
                      phone={c.phone}
                      name={c.name}
                      contactId={c.id}
                      contactType={c.contact_type}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-red-500/10 text-red-300 hover:bg-red-500/20 transition-colors"
                      testid={`record-call-contact-${c.id}`}
                    />
                  )}
                  {c.phone && (
                    <a
                      href={`sms:${c.phone}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`text-contact-${c.id}`}
                    >
                      <MessageSquare size={12} /> Text
                    </a>
                  )}
                  {(c.whatsapp || c.phone) && (
                    <a
                      href={`https://wa.me/${waNumber(c.whatsapp || c.phone)}?text=${encodeURIComponent(`Hello ${c.name}, `)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#10b981]/10 trade-green hover:bg-[#10b981]/20 transition-colors"
                      data-testid={`whatsapp-contact-${c.id}`}
                    >
                      <MessageCircle size={12} /> WhatsApp
                    </a>
                  )}
                  {c.telegram && (
                    <a
                      href={tgLink(c.telegram)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#24A1DE]/10 text-[#24A1DE] hover:bg-[#24A1DE]/20 transition-colors"
                      data-testid={`telegram-contact-${c.id}`}
                    >
                      <Send size={12} /> Telegram
                    </a>
                  )}
                  {c.email && (
                    <a
                      href={`mailto:${c.email}?subject=${encodeURIComponent(emailSubject)}`}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37]/10 trade-gold hover:bg-[#D4AF37]/20 transition-colors"
                      data-testid={`email-contact-${c.id}`}
                    >
                      <Mail size={12} /> Email
                    </a>
                  )}
                  {type === "accountant" && (
                    <button
                      onClick={() => setDocsTarget(c)}
                      className="flex items-center gap-1.5 px-3 py-2 text-xs rounded-sm bg-[#D4AF37] text-black font-semibold hover:bg-[#C5A059] transition-colors"
                      data-testid={`attach-file-btn-${c.id}`}
                    >
                      <Paperclip size={12} /> Attach File
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {children}

        {/* Add / Edit modal */}
        {modalOpen && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setModalOpen(false)}
            data-testid="contact-modal"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="trade-card rounded-sm p-6 w-full max-w-lg max-h-[85vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif font-semibold text-lg">
                  {editing ? "Edit" : "Add"} {type === "accountant" ? "Accountant" : type === "important" ? "Important Person" : "Government Helper"}
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="p-2 text-white/40 hover:text-white transition-colors"
                  data-testid="contact-modal-close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <Label className="text-xs text-white/60">Name *</Label>
                  <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="contact-name-input" />
                </div>
                <div>
                  <Label className="text-xs text-white/60">{orgLabel}</Label>
                  <Input value={form.organization} onChange={e => setForm({...form, organization: e.target.value})} placeholder={orgPlaceholder} className="trade-input mt-1 h-11 rounded-sm" data-testid="contact-org-input" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-white/60">Phone</Label>
                    <Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} placeholder="+251 9X XXX XXXX" className="trade-input mt-1 h-11 rounded-sm" data-testid="contact-phone-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Email</Label>
                    <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="trade-input mt-1 h-11 rounded-sm" data-testid="contact-email-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">WhatsApp</Label>
                    <Input value={form.whatsapp} onChange={e => setForm({...form, whatsapp: e.target.value})} placeholder="+251 9X XXX XXXX" className="trade-input mt-1 h-11 rounded-sm" data-testid="contact-whatsapp-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-white/60">Telegram</Label>
                    <Input value={form.telegram} onChange={e => setForm({...form, telegram: e.target.value})} placeholder="@username or phone" className="trade-input mt-1 h-11 rounded-sm" data-testid="contact-telegram-input" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-white/60">Notes</Label>
                  <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={3} placeholder="What they handle, fees, office hours..." className="trade-input mt-1 rounded-sm" data-testid="contact-notes-input" />
                </div>
                <Button onClick={handleSave} disabled={saving} className="w-full trade-btn-primary rounded-sm h-12" data-testid="save-contact-btn">
                  <Save size={16} className="mr-2" />
                  {saving ? "Saving..." : "Save Contact"}
                </Button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Attach file modal — shared picker */}
        {docsTarget && (
          <SendDocumentModal contact={docsTarget} onClose={() => setDocsTarget(null)} />
        )}
      </div>
    </div>
  );
};

// Ethiopian Export Expense & Gain calculator — preview before you commit
const ExportCalculator = () => {
  const { API, authHeader } = useAuth();
  const [vals, setVals] = useState({
    product_cost: "", transport: "", customs: "", bank: "", agent: "", other: "",
    sale_usd: "", rate: "128",
  });
  const [savingNote, setSavingNote] = useState(false);

  // Prefill exchange rate from live rates
  useEffect(() => {
    axios.get(`${API}/trade/exchange-rates`, { headers: authHeader() })
      .then(res => { if (res.data?.rates?.etb) setVals(v => ({ ...v, rate: String(res.data.rates.etb) })); })
      .catch(() => {});
  }, []);

  const num = (k) => parseFloat(vals[k]) || 0;
  const fmt = (n) => n.toLocaleString("en-US", { maximumFractionDigits: 0 });

  const totalExpense = num("product_cost") + num("transport") + num("customs") + num("bank") + num("agent") + num("other");
  const revenueETB = num("sale_usd") * num("rate");
  const gain = revenueETB - totalExpense;
  const margin = revenueETB > 0 ? (gain / revenueETB) * 100 : null;

  const fields = [
    { key: "product_cost", label: "Goods Cost (ETB)", testid: "exp-product-input" },
    { key: "transport", label: "Inland Transport to Port (ETB)", testid: "exp-transport-input" },
    { key: "customs", label: "Export Customs & Documentation (ETB)", testid: "exp-customs-input" },
    { key: "bank", label: "Bank Charges / LC Fees (ETB)", testid: "exp-bank-input" },
    { key: "agent", label: "Agent / Commission (ETB)", testid: "exp-agent-input" },
    { key: "other", label: "Other Expenses (ETB)", testid: "exp-other-input" },
    { key: "sale_usd", label: "Export Sale Price (USD)", testid: "exp-sale-input" },
    { key: "rate", label: "Rate (1 USD = ? ETB)", testid: "exp-rate-input" },
  ];

  const saveToNotes = async () => {
    setSavingNote(true);
    const text = `Export Estimate\nGoods: ETB ${fmt(num("product_cost"))} | Transport: ${fmt(num("transport"))} | Customs: ${fmt(num("customs"))} | Bank: ${fmt(num("bank"))} | Agent: ${fmt(num("agent"))} | Other: ${fmt(num("other"))}\nTotal Expenses: ETB ${fmt(totalExpense)}\nSale: $${num("sale_usd").toLocaleString()} x ${num("rate")} = ETB ${fmt(revenueETB)}\nGain: ETB ${fmt(gain)}${margin !== null ? ` (${margin.toFixed(1)}%)` : ""}`;
    try {
      await axios.post(`${API}/trade/notes`, { text }, { headers: authHeader() });
      toast.success("Saved to Notes tab");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingNote(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="trade-card rounded-sm p-6 mt-10"
      data-testid="export-calc"
    >
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-[#D4AF37]/10 rounded-sm flex items-center justify-center">
          <Calculator className="w-5 h-5 trade-gold" strokeWidth={1.5} />
        </div>
        <div>
          <h3 className="font-serif font-semibold text-lg">Export Expense & Gain</h3>
          <p className="text-xs text-white/50">Estimate Ethiopian export costs and profit before you commit</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
        <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-4">
          {fields.map(f => (
            <div key={f.key}>
              <Label className="text-xs text-white/60">{f.label}</Label>
              <Input
                type="number"
                step="0.01"
                value={vals[f.key]}
                onChange={e => setVals({ ...vals, [f.key]: e.target.value })}
                className="trade-input mt-1 h-11 rounded-sm"
                data-testid={f.testid}
              />
            </div>
          ))}
        </div>

        <div className="trade-card rounded-sm p-5 h-fit" data-testid="export-summary">
          <h4 className="text-xs uppercase tracking-widest trade-gold mb-4">Summary</h4>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-white/60">Total Expenses</span>
              <span className="font-mono">ETB {fmt(totalExpense)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white/60">Export Revenue</span>
              <span className="font-mono">ETB {fmt(revenueETB)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t border-white/10">
              <span className={gain >= 0 ? "trade-green font-semibold" : "text-red-400 font-semibold"}>Gain</span>
              <span className={`font-mono font-bold ${gain >= 0 ? "trade-green" : "text-red-400"}`} data-testid="export-gain">
                {gain >= 0 ? "+" : "-"}ETB {fmt(Math.abs(gain))}
              </span>
            </div>
            {margin !== null && (
              <p className="text-[11px] text-white/40 text-right">{margin.toFixed(1)}% margin</p>
            )}
          </div>
          <Button
            onClick={saveToNotes}
            disabled={savingNote || (totalExpense === 0 && revenueETB === 0)}
            className="w-full trade-btn-primary rounded-sm h-10 mt-5 text-xs"
            data-testid="save-calc-note-btn"
          >
            <Save size={14} className="mr-2" /> Save Estimate to Notes
          </Button>
        </div>
      </div>
    </motion.div>
  );
};

export const AccountantPage = () => (
  <ContactList
    type="accountant"
    title="Accountant"
    subtitle="Send tax filing information in one tap — email, WhatsApp, Telegram or text"
    orgLabel="Firm"
    orgPlaceholder="Accounting firm or office"
    icon={Receipt}
    emailSubject="Tax Filing Information"
  >
    <ExportCalculator />
  </ContactList>
);

export const GovHelpersPage = () => (
  <ContactList
    type="gov_helper"
    title="Government Helpers"
    subtitle="People who speed up government processes — permits, licenses, paperwork"
    orgLabel="Office / Agency"
    orgPlaceholder="e.g. Customs office, MoTRI, city administration"
    icon={Zap}
    emailSubject="Goldspot Trade — Process Follow-up"
    compact
  />
);

export const ImportantPeoplePage = () => (
  <ContactList
    type="important"
    title="Important People"
    subtitle="Key people who matter to your business — officials, partners, decision makers"
    orgLabel="Organization / Role"
    orgPlaceholder="e.g. Ministry official, bank manager, port authority"
    icon={Star}
    emailSubject="Goldspot Trade — Follow-up"
    compact
  />
);

// Combined page: Government Helpers + Important People behind one sidebar tab
export const GovContactsPage = () => {
  const [sub, setSub] = useState("gov_helper");
  return (
    <div>
      <div className="pt-32 lg:pt-24 lg:ml-64 px-6 md:px-12 max-w-5xl">
        <div className="flex gap-2" data-testid="gov-sub-tabs">
          <button
            onClick={() => setSub("gov_helper")}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs rounded-sm transition-all ${
              sub === "gov_helper"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="sub-tab-gov"
          >
            <Zap size={14} /> Government Helpers
          </button>
          <button
            onClick={() => setSub("important")}
            className={`flex items-center gap-2 px-4 py-2.5 text-xs rounded-sm transition-all ${
              sub === "important"
                ? "bg-[#D4AF37] text-black font-semibold"
                : "bg-white/5 text-white/60 hover:text-white"
            }`}
            data-testid="sub-tab-important"
          >
            <Star size={14} /> Important People
          </button>
        </div>
      </div>
      {sub === "gov_helper" ? <GovHelpersPage /> : <ImportantPeoplePage />}
    </div>
  );
};
